/**
 * URL-backed view state — the ONE way a page keeps navigable state.
 *
 * "Navigable" = anything a person would expect Back to undo, Reload to keep,
 * or a copied link to reproduce: the tab, the view mode, the open record, the
 * search, the filter. That state lives in the URL and NOWHERE else. These
 * hooks DERIVE it from the location on every render instead of copying it
 * into `useState`, which is the bug they exist to retire: state initialised
 * from the URL once (`useState(() => params.get(...))`) renders the right
 * view on load and then ignores Back/Forward forever.
 *
 * Conventions (CLAUDE.md "Deep links"):
 * - path  = WHICH record (`/projects/:slug`), query = HOW it is viewed
 *   (`?tab=`, `?view=`, filters), hash = WHERE on the page (scroll anchor).
 * - "push" (default) for a step a person takes — switching tab, opening an
 *   item. "replace" for a refinement while typing and for canonicalisation.
 * - A value equal to the fallback is OMITTED, so a default view has a clean
 *   URL; a value outside `allowed` reads as the fallback, so a stale or
 *   hand-edited link degrades instead of rendering a blank page.
 * - A remembered preference (localStorage) only fills the gap when the URL is
 *   silent AND the person ARRIVED at that URL (first load, or a link/PUSH) —
 *   never on Back/Forward, where a silent URL means the default it showed —
 *   and is then written into the URL with `replace`, so the address bar never
 *   disagrees with the screen. The URL always wins.
 */

import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext, useLocation, useNavigate } from "react-router-dom";
import { useIsArrival } from "./useIsArrival";

export type UrlHistory = "push" | "replace";

export interface UrlWriteOptions {
	history?: UrlHistory;
}

/** `null`/`undefined`/`""` remove the key. */
export type UrlPatch = Record<string, string | null | undefined>;

/**
 * Writes made in the same tick must compose: the router's location does not
 * advance until the next render, so two setters called from one click would
 * otherwise each start from the same stale search and the second would erase
 * the first. Shared across every hook instance (a page with a tab hook and a
 * selection hook writes both in one handler), keyed by the router AND the
 * location it was computed from, and dropped as soon as that router commits
 * a new location — so it can never outlive the tick it describes. (Every
 * router's first location has the key "default"; keyed by location alone, a
 * write left over from a router that has since unmounted made a NEW router's
 * first write look like a no-op, which is how this was found.)
 */
let pending: { navigator: unknown; key: string; search: string } | null = null;

function normalise(search: string): string {
	if (!search || search === "?") return "";
	return search.startsWith("?") ? search : `?${search}`;
}

/** Low-level: apply a mutation to the current query string as one navigation. */
export function useSearchWriter(): (
	mutate: (params: URLSearchParams) => void,
	options?: UrlWriteOptions,
) => void {
	const location = useLocation();
	const navigate = useNavigate();
	const { navigator } = useContext(UNSAFE_NavigationContext);
	const locationRef = useRef(location);
	locationRef.current = location;

	useLayoutEffect(() => {
		if (pending && pending.navigator === navigator && pending.key !== location.key) pending = null;
	}, [navigator, location.key]);

	return useCallback(
		(mutate, options) => {
			const loc = locationRef.current;
			const composing = pending !== null && pending.navigator === navigator && pending.key === loc.key;
			const base = composing && pending ? pending.search : normalise(loc.search);
			const params = new URLSearchParams(base);
			mutate(params);
			const next = normalise(params.toString());
			if (next === base) return; // never push a duplicate history entry
			pending = { navigator, key: loc.key, search: next };
			// A second write in the same tick amends the entry the first one
			// created, so one click is one Back step.
			const replace = composing || options?.history === "replace";
			navigate({ pathname: loc.pathname, search: next, hash: loc.hash }, { replace });
		},
		[navigate, navigator],
	);
}

/** Set or clear several keys in ONE history entry (switch tab + drop selection). */
export function useUrlPatch(): (patch: UrlPatch, options?: UrlWriteOptions) => void {
	const write = useSearchWriter();
	return useCallback(
		(patch, options) =>
			write((params) => {
				for (const [key, value] of Object.entries(patch)) {
					if (value === null || value === undefined || value === "") params.delete(key);
					else params.set(key, value);
				}
			}, options),
		[write],
	);
}

function readRemembered<T extends string>(key: string | undefined, allowed?: readonly T[]): T | null {
	if (!key) return null;
	try {
		const stored = localStorage.getItem(key);
		if (stored && (!allowed || allowed.includes(stored as T))) return stored as T;
	} catch {
		/* storage unavailable (private mode, blocked site data) */
	}
	return null;
}

function remember(key: string | undefined, value: string): void {
	if (!key) return;
	try {
		localStorage.setItem(key, value);
	} catch {
		/* storage unavailable */
	}
}

export interface UrlEnumOptions<T extends string> {
	/** Shown when the URL is silent or invalid; omitted from the URL when chosen. */
	fallback: T;
	/** Closed vocabulary; anything else reads as the fallback. */
	allowed: readonly T[];
	/** Default "push": a tab or view switch is a step Back should undo. */
	history?: UrlHistory;
	/** localStorage key for a per-device preference used when the URL is silent. */
	rememberAs?: string;
	/** False while `allowed` is still growing (a collection whose kanban view
	 *  appears once its schema loads): the hook then shows the fallback but
	 *  leaves the URL alone, so a valid `?view=kanban` link is not stripped
	 *  before the page knows it is valid. Default true. */
	ready?: boolean;
}

/**
 * One closed-vocabulary query param: a tab, a view mode, a section.
 * Returns the value the screen should show and a setter that navigates.
 */
export function useUrlEnum<T extends string>(
	key: string,
	{ fallback, allowed, history = "push", rememberAs, ready = true }: UrlEnumOptions<T>,
): readonly [T, (next: T, options?: UrlWriteOptions) => void] {
	const location = useLocation();
	const write = useSearchWriter();
	// The preference fills a silent URL only on ARRIVAL (first load, a link):
	// on Back/Forward the silent URL meant the default it showed, and applying
	// the preference re-wrote the URL Back had just restored, so Back appeared
	// to do nothing (measured 2026-10-02 on every page with a remembered view).
	const arrived = useIsArrival();
	const raw = new URLSearchParams(location.search).get(key);
	const fromUrl = raw !== null && allowed.includes(raw as T) ? (raw as T) : null;
	const remembered = fromUrl === null && arrived ? readRemembered(rememberAs, allowed) : null;
	const value: T = fromUrl ?? remembered ?? fallback;

	// Remember what is SHOWN, not only what was clicked, so a reload after
	// Back agrees with the screen it replaces. Not while `allowed` is still
	// growing — the fallback shown then is a placeholder, not a choice.
	useEffect(() => {
		if (ready) remember(rememberAs, value);
	}, [ready, rememberAs, value]);

	// Canonicalise: an invalid value is dropped, a remembered preference is
	// written in — both with replace, so neither costs a Back step.
	const canonical = value === fallback ? null : value;
	useEffect(() => {
		if (!ready || (raw ?? null) === canonical) return;
		write(
			(params) => {
				if (canonical === null) params.delete(key);
				else params.set(key, canonical);
			},
			{ history: "replace" },
		);
	}, [ready, raw, canonical, key, write]);

	const set = useCallback(
		(next: T, options?: UrlWriteOptions) => {
			// Before navigating: choosing the fallback pushes a URL without the
			// key, which IS an arrival, so the preference must already say so.
			remember(rememberAs, next);
			write(
				(params) => {
					if (next === fallback) params.delete(key);
					else params.set(key, next);
				},
				{ history: options?.history ?? history },
			);
		},
		[write, key, fallback, history, rememberAs],
	);
	return [value, set] as const;
}

export interface UrlStringOptions {
	/** Default "push" — opening a record is a step. Pass "replace" for search text. */
	history?: UrlHistory;
}

/**
 * One free-form query param: the open record (`?item=`), a search query, a
 * date. `null` means absent; setting `null` or `""` removes the key.
 */
export function useUrlString(
	key: string,
	{ history = "push" }: UrlStringOptions = {},
): readonly [string | null, (next: string | null, options?: UrlWriteOptions) => void] {
	const location = useLocation();
	const write = useSearchWriter();
	const raw = new URLSearchParams(location.search).get(key);
	const value = raw === null || raw === "" ? null : raw;
	const set = useCallback(
		(next: string | null, options?: UrlWriteOptions) =>
			write(
				(params) => {
					if (next === null || next === "") params.delete(key);
					else params.set(key, next);
				},
				{ history: options?.history ?? history },
			),
		[write, key, history],
	);
	return [value, set] as const;
}

/**
 * A text box whose value belongs in the URL (`?q=`). The input must NOT be
 * bound to the URL directly: this router applies location changes inside a
 * transition, so a controlled input fed from the location reverts each
 * keystroke and jumps the caret; and Safari throws once a page calls
 * `replaceState` 100 times in 30 s. So the box keeps a local draft (instant
 * typing), the URL follows it after a pause with replace (no Back step per
 * keystroke), and a URL change from elsewhere — Back, a link — replaces the
 * draft. Filter with `value`; it is the draft, so results never lag typing.
 */
export function useUrlSearch(
	key: string,
	{ debounceMs = 300 }: { debounceMs?: number } = {},
): readonly [string, (next: string) => void] {
	const [fromUrl, setUrl] = useUrlString(key, { history: "replace" });
	const { pathname } = useLocation();
	const pathnameRef = useRef(pathname);
	pathnameRef.current = pathname;
	const urlValue = fromUrl ?? "";
	const [draft, setDraft] = useState(urlValue);
	const written = useRef(urlValue);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (urlValue === written.current) return;
		// Changed by something other than this box (Back, a link).
		written.current = urlValue;
		if (timer.current) clearTimeout(timer.current);
		setDraft(urlValue);
	}, [urlValue]);

	// A pending write belongs to the path it was typed on. A page that stays
	// mounted across sibling routes (the finance tabs) would otherwise stamp
	// the half-typed ?q= onto the tab the person just switched to.
	useEffect(() => () => {
		if (timer.current) clearTimeout(timer.current);
	}, [pathname]);

	const set = useCallback(
		(next: string) => {
			setDraft(next);
			written.current = next;
			if (timer.current) clearTimeout(timer.current);
			const typedOn = pathnameRef.current;
			timer.current = setTimeout(() => {
				if (pathnameRef.current === typedOn) setUrl(next || null);
			}, debounceMs);
		},
		[setUrl, debounceMs],
	);
	return [draft, set] as const;
}

/** Test seam: forget a composed-but-unrendered write between tests. */
export function __resetUrlStateForTests(): void {
	pending = null;
}
